import { inject, Injectable, signal } from '@angular/core';
import { LoggerService } from './logger.service';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  autoDismiss?: boolean;
  dismissAfter?: number; // milliseconds
  actions?: ToastAction[];
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private toasts = signal<Toast[]>([]);
  private logger = inject(LoggerService).getLogger('toast');

  // Public readonly signal
  readonly activeToasts = this.toasts.asReadonly();

  private nextId = 0;

  show(message: string, type: ToastType = 'info', autoDismiss = true, dismissAfter = 5000, actions?: ToastAction[]): string {
    const id = `toast-${this.nextId++}`;
    const toast: Toast = {
      id,
      message,
      type,
      autoDismiss,
      dismissAfter,
      actions
    };

    this.logger.debug('Toast shown:', { id, message, type, autoDismiss, dismissAfter, actions: actions?.length });
    this.toasts.update(toasts => [...toasts, toast]);
    this.logger.debug('Active toasts:', this.toasts().length);

    if (autoDismiss && dismissAfter) {
      setTimeout(() => this.dismiss(id), dismissAfter);
    }

    return id;
  }

  success(message: string, autoDismiss = true, dismissAfter = 5000, actions?: ToastAction[]): string {
    return this.show(message, 'success', autoDismiss, dismissAfter, actions);
  }

  error(message: string, autoDismiss = true, dismissAfter = 7000, actions?: ToastAction[]): string {
    return this.show(message, 'error', autoDismiss, dismissAfter, actions);
  }

  warning(message: string, autoDismiss = true, dismissAfter = 6000, actions?: ToastAction[]): string {
    return this.show(message, 'warning', autoDismiss, dismissAfter, actions);
  }

  info(message: string, autoDismiss = true, dismissAfter = 5000, actions?: ToastAction[]): string {
    return this.show(message, 'info', autoDismiss, dismissAfter, actions);
  }

  dismiss(id: string): void {
    this.toasts.update(toasts => toasts.filter(t => t.id !== id));
  }

  clear(): void {
    this.toasts.set([]);
  }
}
